import { parseDate } from "@internationalized/date";
import type { UseInfiniteQueryResult } from "@tanstack/react-query";
import cn from "classnames";
import type { FilterCondition } from "epsm-api-types";
import {
	SCREENER_COLUMNS,
	type ScreenerColumn,
	type ScreenerFilterData,
} from "epsm-api-types/models";
import { type ChangeEvent, useEffect, useState } from "react";
import type { SingleValue } from "react-select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { parseNumber } from "@/components/widgets/helpers/FormattingHelpers";
import {
	type FilterDataContextProps,
	useFilterData,
} from "@/context/FilterDataContext";
import {
	useWidgetView,
	type ViewState,
	type WidgetViewContextProps,
} from "@/context/WidgetViewContext";
import type { Option } from "@/interfaces/Option";
import type { ValueRange } from "@/interfaces/ValueRange";
import { getCurrentTimeInMs, getISODate, ONE_DAY_IN_MS } from "@/lib/utils";
import { flattenSubfieldsToDropdownOptions } from "@/lib/utils/flattenSubfieldsToDropdownOptions";
import { convertToRelativeDateInDays } from "../../lib/helpers/convertToRelativeDateInDays";
import { isEmptyInputValue } from "../../lib/helpers/isEmptyInputValue";
import { isRelativeDate } from "../../lib/helpers/isRelativeDate";
import { DatePicker } from "./ui/DatePicker/DatePicker";
import { Dropdown } from "./ui/Dropdown";
import { RangePicker } from "./ui/RangePicker";

const SCREEN_WIDTHS = {
	SMALL: 831,
	MEDIUM: 1200,
	LARGE: 1600,
};

export const numberInputRegExp: RegExp = /^-?(0(\.\d*)?|[1-9]\d*(\.\d*)?)$/;

export const DateFilters = ({
	width,
	refetch,
	hasNull,
}: {
	width?: number;
	refetch: UseInfiniteQueryResult["refetch"];
	hasNull: (data: ScreenerFilterData | null) => boolean | null;
}) => {
	const { selectedColumn, isRelative, setIsRelative } = useWidgetView() as {
		selectedColumn: ViewState<ScreenerColumn>;
	} & WidgetViewContextProps;
	const {
		updateResults,
		value,
		setValue,
		filterData,
		isDate,
		selectedSubField,
		setSelectedSubField,
		getSubFieldName,
	} = useFilterData() as {
		filterData: ScreenerFilterData;
	} & FilterDataContextProps;

	const [inputValue, setInputValue] = useState<ValueRange>(value);

	useEffect(() => {
		if (!isDate()) return;

		const now = getCurrentTimeInMs();

		if (isRelativeDate(value?.min) && isRelativeDate(value?.max)) {
			setValue({
				min: String(+value.min + now),
				max: String(+value.max + now),
			});
		}
	}, [isDate, value?.min, value?.max, setValue]);

	useEffect(() => {
		if (isRelative) {
			setInputValue({
				min: convertToRelativeDateInDays(value.min),
				max: convertToRelativeDateInDays(value.max),
			});
		}
	}, [value, isRelative]);

	const getParsedDate = (value: string) => {
		const date = getISODate(value).split("T")[0];
		const parsedDate = parseDate(date);

		return parsedDate;
	};

	const handleUpdateResults = () => {
		const newResult: Array<{
			name: string;
			condition: FilterCondition;
			value: unknown;
		}> = [];

		let minValue: string | number;
		let maxValue: string | number;

		if (isDate()) {
			if (isRelative) {
				if (!isEmptyInputValue(inputValue.min)) {
					const daysInMs = Number(inputValue.min) * ONE_DAY_IN_MS;
					minValue = daysInMs < 0 ? `${daysInMs}` : `+${daysInMs}`;

					newResult.push({
						name: getSubFieldName() ?? selectedColumn.Screener.name,
						condition: "gte",
						value: minValue,
					});
				}
				if (!isEmptyInputValue(inputValue.max)) {
					const daysInMs = Number(inputValue.max) * ONE_DAY_IN_MS;
					maxValue = daysInMs < 0 ? `${daysInMs}` : `+${daysInMs}`;

					newResult.push({
						name: getSubFieldName() ?? selectedColumn.Screener.name,
						condition: "lt",
						value: maxValue,
					});
				}
			} else {
				if (!isEmptyInputValue(value.min)) {
					minValue = parseNumber(value.min);
					newResult.push({
						name: getSubFieldName() ?? selectedColumn.Screener.name,
						condition: "gte",
						value: minValue,
					});
				}

				if (!isEmptyInputValue(value.max)) {
					maxValue = parseNumber(value.max);
					newResult.push({
						name: getSubFieldName() ?? selectedColumn.Screener.name,
						condition: "lt",
						value: maxValue,
					});
				}
			}
		}

		updateResults(newResult, refetch);
	};

	const subFields = flattenSubfieldsToDropdownOptions(
		selectedColumn?.Screener?.subFields || [],
	);

	const column = SCREENER_COLUMNS.find(
		(column) => column.name === selectedColumn?.Screener?.name,
	);

	const columnAbbr =
		column?.titleAbbreviation ?? column?.titleShort ?? column?.title;
	const columnTitle =
		column?.title === columnAbbr
			? column?.title
			: `${column?.title} (${columnAbbr})`;

	const handleRelativeDateInputChange = (
		event: ChangeEvent<HTMLInputElement>,
		item: string,
	) => {
		const inputVal = parseNumber(event.target.value);

		if (inputVal === "" || inputVal === "-") {
			setInputValue((prev) => ({
				...prev,
				[item]: String(inputVal),
			}));

			return;
		}

		if (!Number.isNaN(inputVal)) {
			setInputValue((prev) => ({
				...prev,
				[item]: String(inputVal),
			}));

			if (typeof inputVal === "number") {
				setValue((prev) => ({
					...prev,
					[item]: String(inputVal * ONE_DAY_IN_MS + getCurrentTimeInMs()),
				}));
			}
		}
	};

	const renderRelativeDateToggleButton = () => {
		return (
			<Button
				variant="outline"
				type="button"
				className="h-[18px] self-end p-1 py-0 "
				onClick={() => setIsRelative((prev) => !prev)}
				onMouseDown={(e) => e.preventDefault()}
			>
				{isRelative ? "AbsoluteD" : "RelativeD"}
			</Button>
		);
	};

	return (
		<div className="flex flex-col w-full" data-testid="date-filters">
			<div
				className={cn("flex items-center", {
					"flex-col": width && width < SCREEN_WIDTHS.LARGE,
				})}
			>
				<div className="flex flex-col items-stretch w-full h-full">
					<div className="flex flex-col gap-4">
						<h3 className="text-[var(--light-gray)]">{columnTitle}</h3>
						{selectedColumn?.Screener?.subFields?.length && (
							<Dropdown
								options={subFields}
								maxMenuHeight={80}
								placeholder={subFields[0]?.label}
								value={selectedSubField || subFields[0]}
								onChange={(newValue) =>
									setSelectedSubField(newValue as SingleValue<Option>)
								}
								isTypeList={false}
								className="w-[120px] self-end mb-2"
							/>
						)}
					</div>

					{width &&
						width <= SCREEN_WIDTHS.LARGE &&
						isDate() &&
						renderRelativeDateToggleButton()}

					<div
						className={cn("flex justify-center gap-1 mb-3 mt-5 w-full", {
							"flex-col mt-0!":
								isDate() && width && width < SCREEN_WIDTHS.MEDIUM,
						})}
					>
						{["Min", "Max"].map((item) => {
							const itemKey = item.toLowerCase() as keyof ValueRange;

							return (
								<div key={item} className="w-full">
									<div className="flex items-start mb-2">
										<p className="text-white/80 mr-3">{item}</p>
									</div>

									{isRelative ? (
										<Input
											className="text-right w-full bg-background"
											data-testid={item}
											value={inputValue[itemKey] ?? ""}
											onChange={(event) =>
												handleRelativeDateInputChange(event, itemKey)
											}
										/>
									) : (
										isDate() &&
										!isRelative && (
											<DatePicker
												aria-label="date-picker"
												data-testid={item}
												value={
													value?.[itemKey] && !Number.isNaN(+value?.[itemKey])
														? getParsedDate(value[itemKey])
														: null
												}
												onChange={(date) => {
													let UTCDate = 0;

													if (date) {
														const newDate = new Date(
															Date.UTC(0, date.month - 1, date.day),
														);
														UTCDate = newDate.setUTCFullYear(date.year);
													}

													setValue((prevValue) => ({
														...prevValue,
														[itemKey]: UTCDate,
													}));
												}}
											/>
										)
									)}
								</div>
							);
						})}
						{width &&
							width > SCREEN_WIDTHS.LARGE &&
							isDate() &&
							renderRelativeDateToggleButton()}
					</div>
					{!!filterData?.items?.length && !hasNull(filterData) && (
						<RangePicker />
					)}
				</div>
			</div>

			<Button
				className={cn("self-end h-[18px]", {
					"self-start": width && width < SCREEN_WIDTHS.SMALL,
				})}
				variant="outline"
				disabled={
					(isEmptyInputValue(inputValue?.min) &&
						isEmptyInputValue(inputValue?.max)) ||
					(isEmptyInputValue(value?.min) && isEmptyInputValue(value?.max))
				}
				onClick={handleUpdateResults}
				onMouseDown={(e) => e.preventDefault()}
			>
				Add
			</Button>
		</div>
	);
};
